import { useState, useEffect } from 'react'
import { User, Building2 } from 'lucide-react'
import { KTSVG } from '../../../../_metronic/helpers'
import {
  AddCompanyMutation,
  AddCompanyMutationVariables,
  AddWatchlistMutation,
  AddWatchlistMutationVariables,
  GlobalCompanySearchType,
  WatchlistObject,
  WatchlistEntityType,
  useAddCompanyMutation,
  useAddWatchlistMutation,
  useGetNewWatchlistsQuery,
  useGetWatchlistsQuery,
  useGetWatchlistByIdQuery,
} from '../../../../generated/graphql'
import { Link } from 'react-router-dom'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import Toast, { ToastType } from '../../../../utils/toast'
import { useCompanyNameStore } from '../../../../store/companyNameStore'

type SearchCompaniesProps = {
  filteredCompanies: GlobalCompanySearchType[]
  currentWatchlist: WatchlistObject
  onSearchClose?: (createdWatchlistName?: string) => void
}

export function GlobalSearchBar(props: SearchCompaniesProps) {
  const [selectedFilter, setSelectedFilter] = useState<WatchlistEntityType | 'all'>('all')
  const [searchResults, setSearchResults] = useState<JSX.Element[] | undefined>([])
  const companyNameStore = useCompanyNameStore()
  const getNewWatchlistQueryKey = useGetNewWatchlistsQuery.getKey()
  const getWatchlistsQueryKey = useGetWatchlistsQuery.getKey()

  // Reset filter to 'all' when search results change (new search)
  useEffect(() => {
    setSelectedFilter('all')
  }, [props.filteredCompanies])

  const mutateAddCompany = useAddCompanyMutation<Error>(graphqlRequestClient, {
    onSuccess: (
      data: AddCompanyMutation,
      variables: AddCompanyMutationVariables,
      _context: unknown
    ) => {
      // Invalidate the main watchlists query
      queryClient.invalidateQueries(getNewWatchlistQueryKey)
      // Invalidate the specific watchlist details query if we have the watchlist ID
      if (variables.input?.id) {
        queryClient.invalidateQueries(
          useGetWatchlistByIdQuery.getKey({ input: { id: variables.input.id } })
        )
      }
      Toast('Company Added To Watchlist Successfully', ToastType.success)
    },
    onError: (error: any) => {
      Toast(`${error.response.errors[0].message}`, ToastType.error)
    },
  })

  const mutateAddWatchlist = useAddWatchlistMutation<Error>(graphqlRequestClient, {
    onSuccess: (
      data: AddWatchlistMutation,
      variables: AddWatchlistMutationVariables,
      _context: unknown
    ) => {
      queryClient.invalidateQueries(getWatchlistsQueryKey)
      queryClient.invalidateQueries(getNewWatchlistQueryKey)
      Toast('Investor added to Watchlist Successfully', ToastType.success)
      // Close the search bar and navigate to the created watchlist
      if (props.onSearchClose) {
        props.onSearchClose(variables.input.name || undefined)
      }
    },
    onError: (error: any) => {
      Toast(`${error.response.errors[0].message}`, ToastType.error)
    },
  })

  function handleAddCompany(e: React.MouseEvent, companyId: string) {
    e.stopPropagation()
    e.preventDefault()
    mutateAddCompany.mutate({
      input: {
        id: props.currentWatchlist.id,
        companyId: companyId,
      },
    })
  }

  function handleAddInvestor(e: React.MouseEvent, investorName: string) {
    e.stopPropagation()
    e.preventDefault()
    mutateAddWatchlist.mutate({
      input: {
        name: investorName,
        type: WatchlistEntityType.Investor,
      },
    })
  }

  function handleAddEntity(e: React.MouseEvent, company: GlobalCompanySearchType) {
    if (String(company.type) === String(WatchlistEntityType.Investor) && company.investorName) {
      handleAddInvestor(e, company.investorName)
    } else {
      handleAddCompany(e, company.id!)
    }
  }

  // Count companies and investors for the tags (using current data for immediate response)
  const companyCount =
    props.filteredCompanies?.filter(
      (company) => String(company.type) === String(WatchlistEntityType.Company)
    ).length || 0
  const investorCount =
    props.filteredCompanies?.filter(
      (company) => String(company.type) === String(WatchlistEntityType.Investor)
    ).length || 0

  // Update search results when filter or data changes
  useEffect(() => {
    // For filtering, prioritize immediate response - use current data if filter changed
    // or use debounced data if search input changed
    const dataToUse = props.filteredCompanies

    // Filter the companies based on selected filter
    const filteredResults = dataToUse?.filter((company) => {
      if (selectedFilter === 'all') return true

      // Handle null/undefined type
      if (!company.type) return false

      // Convert both to strings for comparison to avoid enum issues
      const companyTypeStr = String(company.type)
      const selectedFilterStr = String(selectedFilter)

      return companyTypeStr === selectedFilterStr
    })

    const results = filteredResults?.map((company: GlobalCompanySearchType, index: number) => {
      const getEntityIcon = () => {
        if (String(company.type) === String(WatchlistEntityType.Investor)) {
          return <User size={16} className='text-gray-600' />
        } else {
          return <Building2 size={16} className='text-gray-600' />
        }
      }

      return (
        <div key={company.id || index} className='d-flex flex-row'>
          <span className='pe-2 p-0 py-2'>
            {props.currentWatchlist && props.currentWatchlist.type !== WatchlistEntityType.Investor && (
              <button
                type='button'
                className=' bg-white border border-0'
                onClick={(e) => {
                  handleAddEntity(e, company)
                }}
              >
                <KTSVG
                  path={'media/icons/duotune/general/gen041.svg'}
                  className='ps-4 svg-icon-2x text-start fs-9 pt-3'
                  svgClassName='text-gray-700 text-hover-primary'
                />
              </button>
            )}
          </span>

          {String(company.type) === String(WatchlistEntityType.Investor) ? (
            <div
              className='fs-base fw-bold text-gray-800 text-hover-primary d-flex p-0 justify-content-center cursor-pointer'
              onClick={(e) => {
                handleAddEntity(e, company)
              }}
            >
              <tr key={`${company.id}-${index}`} className='bg-hover-primary-light py-2'>
                <td className='w-350px min-w-lg-650px px-2'>
                  <div className='d-flex align-items-center'>
                    {getEntityIcon()}
                    <div className='ms-2'>
                      <p className='mb-0'>{company.name}</p>
                      <div className='fs-8 text-gray-500'>{company.dba}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </div>
          ) : (
            <Link
              className='fs-base fw-bold text-gray-800 text-hover-primary d-flex p-0 justify-content-center'
              to={`/company/summary/HISSA/${company.id}`}
              onClick={() => {
                companyNameStore.setCompanyName(company.name || '')
                companyNameStore.setCompanyCurrency(company.companyCurrency || 'INR - ₹')
              }}
            >
              <tr key={`${company.id}-${index}`} className='bg-hover-primary-light py-2'>
                <td className='w-350px min-w-lg-650px px-2'>
                  <div className='d-flex align-items-center'>
                    {getEntityIcon()}
                    <div className='ms-2'>
                      <p className='mb-0'>{company.name}</p>
                      <div className='fs-8 text-gray-500'>{company.dba}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </Link>
          )}
        </div>
      )
    })

    setSearchResults(results)
  }, [props.filteredCompanies, selectedFilter, props.currentWatchlist])

  return (
    <>
      <div className='separator'></div>
      {props.filteredCompanies.length > 0 && (
        <div className='card w-675px mh-325px bg-white overflow-auto p-1'>
          {/* Filter Tags */}
          <div className='d-flex align-items-center gap-1 p-2 border-bottom'>
            <span className='text-gray-600 fw-semibold fs-8 me-1'>Filter:</span>
            <button
              className={`btn btn-sm border ${
                selectedFilter === 'all'
                  ? 'btn-primary border-primary'
                  : 'btn-outline-primary border-primary'
              }`}
              onClick={() => setSelectedFilter('all')}
            >
              All ({props.filteredCompanies.length})
            </button>
            {companyCount > 0 && (
              <button
                className={`btn btn-sm border ${
                  selectedFilter === WatchlistEntityType.Company
                    ? 'btn-primary border-primary'
                    : 'btn-outline-primary border-primary'
                }`}
                onClick={() => setSelectedFilter(WatchlistEntityType.Company)}
              >
                <Building2 size={16} className='me-1' />
                Company ({companyCount})
              </button>
            )}
            {investorCount > 0 && (
              <button
                className={`btn btn-sm border ${
                  selectedFilter === WatchlistEntityType.Investor
                    ? 'btn-primary border-primary'
                    : 'btn-outline-primary border-primary'
                }`}
                onClick={() => setSelectedFilter(WatchlistEntityType.Investor)}
              >
                <User size={16} className='me-1' />
                Investor ({investorCount})
              </button>
            )}
          </div>

          <table className='table align-middle table-row-dashed align-items-baseline'>
            <tbody>{searchResults}</tbody>
          </table>
        </div>
      )}
    </>
  )
}
